/** Targets are requirements; live measurements determine whether they are met. */
export const ECOURTS_LOOKUP_TARGET_MS = 15_000;
export const ECOURTS_LOOKUP_DEADLINE_MS = 60_000;
export const ECOURTS_PROGRESS_DEADLINE_MS = 20_000;
export const ECOURTS_CHECKPOINT_BUDGET_MS = 5_000;
export const ECOURTS_MAX_CONCURRENCY = 4;

export type EcourtsRuntimeStatus =
  | "idle"
  | "starting"
  | "working"
  | "warming"
  | "retrying"
  | "degraded"
  | "unavailable";

export type EcourtsRuntimeMonitor = {
  sampledAt: string;
  status: EcourtsRuntimeStatus;
  concurrency: number;
  active: Array<{
    runId: string;
    caseNumber: string;
    stage: string;
    elapsedMs: number;
    progressAgeMs: number;
  }>;
  browser: {
    connected: boolean;
    extensionLoaded: boolean;
    /** Whether this runtime requires the custom extension for its active path. */
    extensionRequired?: boolean;
    /** On-disk install; null when the worker has not checked. Closed browser ≠ missing. */
    extensionInstalled?: boolean | null;
    classifierReady: boolean;
    pages: number;
  };
  performance: {
    sampleSize: number;
    p50Ms: number | null;
    p95Ms: number | null;
    underTargetPercent: number | null;
    completedPerMinute: number | null;
    failures: number;
    timeouts: number;
    /** Rolling attempt window. Missing measurements stay null; 0 means zero in this window. */
    windowMs?: number;
    maxSamples?: number;
    processStartedAt?: string;
  };
  resources: {
    memoryBytes: number | null;
    memoryLimitBytes: number | null;
    /** 100 = one busy CPU, not 100% of the host. */
    cpuPercent: number | null;
    cpuCount?: number | null;
    eventLoopDelayMs: number | null;
    hostAvailableMemoryBytes: number | null;
  };
  alerts: string[];
  lastError: string | null;
  lastErrorAt?: string | null;
  lastSuccessAt?: string | null;
};

export type EcourtsHealth = {
  sampledAt: string;
  queued: number;
  running: number;
  /** Queue work split for the shared eCourts Retrieval lane. */
  subcounts?: EcourtsQueueSubcounts;
  oldestQueuedAt: string | null;
  runtime: EcourtsRuntimeMonitor | null;
  alerts: string[];
  lastSuccessAt?: string | null;
  lastFailureAt?: string | null;
  lastFailureClass?: string | null;
  executionBackend?: "vps";
  ledger?: "sqlite";
  releaseRevision?: string | null;
};

export type EcourtsQueueSubcounts = {
  /** Shared browser retrieval jobs currently queued or running. */
  retrievalPending: number;
  /** Stored case, version mismatch, no leftover failure flags. Clean and ready to write. */
  caseStoredAwaitingPropertyUpsert: number;
  /** Linked properties with a canonical eCourts projection applied. */
  upsertComplete: number;
  /** collection_stage failed/partial/missing_required, or missing PDFs / partial collection. Needs another fetch. */
  incompleteRetrieval: number;
  /** fact_freshness = uncertain. Needs a new accepted retrieval. */
  uncertainFacts: number;
  /** Transient fetch: ecourts_page_inaccessible, ecourts_browser_lease_expired. */
  retryableService: number;
  /** ecourts_lookup_timeout / ecourts_extension_failure. Do not ordinary-retry. */
  terminalInfra: number;
  /** ecourts_auction_no_result / ecourts_auction_date_miss / toolMisses.ecourts_auction_date. */
  houseMiss: number;
  /** ecourts_upsert_pending or stored case with version mismatch, without leftover failure flags. */
  upsertPending: number;
  /** ecourts_sp_plv_% on a not-upserted property. Analysis hop, not retrieval. */
  analysisLeak: number;
  /** ecourts_auction_date_pending with no queued/running tool_run. */
  pendingNoJob: number;
  /** ecourts_* class and no ecourts_case_properties row. */
  neverLinked: number;
  /** Remaining ecourts_% not classified above. */
  otherEcourts: number;
};
